import { Descriptions, Popover, Space, Spin, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IApplicationPropertyValuer from '~Api/Application/IApplicationPropertyValuer';
import ValuerStatusEnum from '~Api/Application/ValuerStatusEnum';
import ConditionTypeEnum from '~Api/Application/ConditionTypeEnum';
import { applicationPropertyValuersListAction } from '~Applications/actions';
import { applicationPropertyValuersSelector } from '~Applications/selectors';
import PropertyEditModal from '~Deals/PropertyEditModal';
import UploadedConditionDocumentList from '~Applications/Application/UploadedConditionDocumentList';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import { valuersListAction } from '~Valuers/actions';
import IValuer from '~Valuers/IValuer';
import { valuersSelector } from '~Valuers/selectors';
import PropertyValuerActions from './PropertyValuerActions';
import UpdateDueDateModal from './UpdateDueDateModal';
import UpdateQuote from './UpdateQuote';
import UpdateValuationValueModal from './UpdateValuationValueModal';
import ValuationInspectionDateModal from './ValuationInspectionDateModal';
import ValuationReceivedDateModal from './ValuationReceivedDateModal';
import { currencyFormatter } from '~utilities/formatters';
import UpdateTimeFrame from './UpdateTimeFrame';
import { useDispatch, useSelector } from 'react-redux';

interface IStatusTag {
    colour: string;
    label: string;
}

const statusTagDetailsMap: IDictionary<IStatusTag> = {
    [ValuerStatusEnum.Cancelled]: {
        colour: 'default',
        label: 'Cancelled',
    },
    [ValuerStatusEnum.QuoteReceived]: {
        colour: 'orange',
        label: 'Quote Received',
    },
    [ValuerStatusEnum.QuoteRequested]: {
        colour: 'orange',
        label: 'Quote Requested',
    },
    [ValuerStatusEnum.Refused]: {
        colour: 'default',
        label: 'Refused',
    },
    [ValuerStatusEnum.ValuationOrdered]: {
        colour: 'orange',
        label: 'Valuation Ordered',
    },
    [ValuerStatusEnum.ValuationReceived]: {
        colour: 'green',
        label: 'Valuation Received',
    },
};

interface IPropertyValuerValuerPair {
    propertyValuer: IApplicationPropertyValuer;
    uid: string;
    valuer: IValuer;
}

export default function PropertyValuation(props: {
    application: IApplication;
    applicationProperty: IApplicationProperty;
}): ReactElement {
    const { application, applicationProperty } = props;

    const dispatch: Dispatch = useDispatch();

    const propertyValuers: IDictionary<IApplicationPropertyValuer> = useSelector(
        (state: IGlobalState): IDictionary<IApplicationPropertyValuer> => applicationPropertyValuersSelector(
            state,
            applicationProperty.uuid,
        ),
    );
    const valuers: IDictionary<IValuer> = useSelector(
        (state: IGlobalState): IDictionary<IValuer> => valuersSelector(state),
    );

    const [ isUpdateDueDateModalOpen, setIsUpdateDueDateModalOpen ] = useState<boolean>(false);
    const [ isUpdateEstimatedValueModalOpen, setIsUpdateEstimatedValueModalOpen ] = useState<boolean>(false);
    const [ isUpdateInspectionDateModalOpen, setIsUpdateInspectionDateModalOpen ] = useState<boolean>(false);
    const [ isUpdateReceivedDateModalOpen, setIsUpdateReceivedDateModalOpen ] = useState<boolean>(false);
    const [ isUpdateValuationValueModalOpen, setIsUpdateValuationValueModalOpen ] = useState<boolean>(false);

    useEffect((): void => {
        if (!propertyValuers) {
            dispatch(applicationPropertyValuersListAction(applicationProperty.uuid));
        }
    }, [
        applicationProperty.uuid,
        dispatch,
        propertyValuers,
    ]);

    useEffect((): void => {
        if (!valuers) {
            dispatch(valuersListAction());
        }
    }, [
        dispatch,
        valuers,
    ]);

    const onClickUpdateDueDate: () => void = useCallback((): void => {
        setIsUpdateDueDateModalOpen(true);
    }, []);

    const onClickUpdateDueDateCancel: () => void = useCallback((): void => {
        setIsUpdateDueDateModalOpen(false);
    }, []);

    const onClickUpdateEstimatedValue: () => void = useCallback((): void => {
        setIsUpdateEstimatedValueModalOpen(true);
    }, []);

    const onClickUpdateEstimatedValueCancel: () => void = useCallback((): void => {
        setIsUpdateEstimatedValueModalOpen(false);
    }, []);

    const onClickUpdateInspectionDate: () => void = useCallback((): void => {
        setIsUpdateInspectionDateModalOpen(true);
    }, []);

    const onClickUpdateInspectionDateCancel: () => void = useCallback((): void => {
        setIsUpdateInspectionDateModalOpen(false);
    }, []);

    const onClickUpdateReceivedDate: () => void = useCallback((): void => {
        setIsUpdateReceivedDateModalOpen(true);
    }, []);

    const onClickUpdateReceivedDateCancel: () => void = useCallback((): void => {
        setIsUpdateReceivedDateModalOpen(false);
    }, []);

    const onClickUpdateValuationValue: () => void = useCallback((): void => {
        setIsUpdateValuationValueModalOpen(true);
    }, []);

    const onClickUpdateValuationValueCancel: () => void = useCallback((): void => {
        setIsUpdateValuationValueModalOpen(false);
    }, []);

    if (!application || !applicationProperty || !valuers) {
        return (
            <Spin />
        );
    }

    const propertyValuerValuerPairs: IPropertyValuerValuerPair[] = _.filter(
        valuers,
        { state: applicationProperty.dealProperty.state },
    ).map((loopValuer: IValuer): IPropertyValuerValuerPair => ({
        propertyValuer: _.find(propertyValuers, { valuerUuid: loopValuer.uuid }),
        uid: _.uniqueId('property-valuer-valuer-pair-'),
        valuer: loopValuer,
    }));

    const columns: ColumnsType<IPropertyValuerValuerPair> = [
        {
            render: (propertyValuerValuerPair: IPropertyValuerValuerPair): JSX.Element => {
                if (
                    !propertyValuerValuerPair.propertyValuer
                    || !propertyValuerValuerPair.propertyValuer.status
                    || propertyValuerValuerPair.propertyValuer.status === ValuerStatusEnum.New
                ) {
                    return null;
                }

                const statusTagDetails: IStatusTag = statusTagDetailsMap[propertyValuerValuerPair.propertyValuer.status];

                return (
                    <Tag color={statusTagDetails.colour}>{statusTagDetails.label}</Tag>
                );
            },
            title: 'Status',
            width: '12%',
        },
        {
            render: (propertyValuerValuerPair: IPropertyValuerValuerPair): JSX.Element => {
                const valuerContentBlock: JSX.Element = (
                    <React.Fragment>
                        <p>Phone: {propertyValuerValuerPair.valuer.phone}</p>
                        <p>Email: {propertyValuerValuerPair.valuer.email}</p>
                    </React.Fragment>
                );

                return (
                    <Popover content={valuerContentBlock} title={propertyValuerValuerPair.valuer.name}>
                        <a>{propertyValuerValuerPair.valuer.name}</a>
                    </Popover>
                );
            },
            title: 'Valuer',
        },
        {
            render: (propertyValuerValuerPair: IPropertyValuerValuerPair): JSX.Element => {
                if (
                    !propertyValuerValuerPair.propertyValuer
                    || !propertyValuerValuerPair.propertyValuer.quoteAmount
                ) {
                    return null;
                }

                return (
                    <UpdateQuote
                        applicationProperty={applicationProperty}
                        propertyValuer={propertyValuerValuerPair.propertyValuer}
                    />
                );
            },
            title: 'Quote',
            width: '12%',
        },
        {
            render: (propertyValuerValuerPair: IPropertyValuerValuerPair): JSX.Element => {
                if (
                    !propertyValuerValuerPair.propertyValuer
                    || !propertyValuerValuerPair.propertyValuer.quoteTimeFrameDays
                ) {
                    return null;
                }

                return (
                    <UpdateTimeFrame
                        applicationProperty={applicationProperty}
                        propertyValuer={propertyValuerValuerPair.propertyValuer}
                    />
                );
            },
            title: 'Time Frame',
            width: '12%',
        },
        {
            render: (propertyValuerValuerPair: IPropertyValuerValuerPair): JSX.Element => {
                if (
                    !propertyValuerValuerPair.propertyValuer
                    || !propertyValuerValuerPair.propertyValuer.valuationType
                ) {
                    return null;
                }

                return (
                    <Tag className='valuation-type-tag'>{propertyValuerValuerPair.propertyValuer.valuationType}</Tag>
                );
            },
            title: 'Type',
            width: '10%',
        },
        {
            render: (propertyValuerValuerPair: IPropertyValuerValuerPair): JSX.Element => (
                <PropertyValuerActions
                    application={application}
                    applicationProperty={applicationProperty}
                    valuation={propertyValuerValuerPair.propertyValuer}
                    valuer={propertyValuerValuerPair.valuer}
                />
            ),
            title: 'Actions',
            width: '12%',
        },
    ];

    const valuationValueItemValue: string = applicationProperty.valuationValue ? (
        currencyFormatter.format(applicationProperty.valuationValue)
    ) : '-';

    const valuationDueDateItemValue: string = applicationProperty.valuationDueDate ? (
        dayjs(applicationProperty.valuationDueDate).format('Do MMMM YYYY')
    ) : '-';

    const inspectionDateItemValue: string = applicationProperty.valuationInspectionDate ? (
        dayjs(applicationProperty.valuationInspectionDate).format('Do MMMM YYYY')
    ) : '-';

    const valuationReceivedDateItemValue: string = applicationProperty.valuationReceivedDate ? (
        dayjs(applicationProperty.valuationReceivedDate).format('Do MMMM YYYY')
    ) : '-';

    const valuationTypeTagBlock: JSX.Element | null = applicationProperty.valuationType ? (
        <Tag className='valuation-type-tag'>
            {applicationProperty.valuationType}
        </Tag>
    ) : null;

    return (
        <div className='property-valuation'>
            <Typography.Title level={3}>Details</Typography.Title>
            <Descriptions
                bordered={true}
                column={3}
                layout='vertical'
                size='small'
            >
                <Descriptions.Item label='Estimated Value'>
                    <a onClick={onClickUpdateEstimatedValue}>
                        {currencyFormatter.format(applicationProperty.dealProperty.estimatedValue)}
                    </a>
                    <PropertyEditModal
                        dealUuid={application.dealUuid}
                        isOpen={isUpdateEstimatedValueModalOpen}
                        onCancel={onClickUpdateEstimatedValueCancel}
                        propertyUuid={applicationProperty.dealPropertyUuid}
                    />
                </Descriptions.Item>
                <Descriptions.Item label='Valuation'>
                    <a onClick={onClickUpdateValuationValue}>
                        <Space>
                            {valuationValueItemValue}
                            {valuationTypeTagBlock}
                        </Space>
                    </a>
                    <UpdateValuationValueModal
                        applicationProperty={applicationProperty}
                        isOpen={isUpdateValuationValueModalOpen}
                        onCancel={onClickUpdateValuationValueCancel}
                    />
                </Descriptions.Item>
                <Descriptions.Item label='Documents'>
                    <UploadedConditionDocumentList
                        application={application}
                        applicationProperty={applicationProperty}
                        conditionType={ConditionTypeEnum.Valuation}
                    />
                </Descriptions.Item>
                <Descriptions.Item label='Due Date'>
                    <a onClick={onClickUpdateDueDate}>
                        {valuationDueDateItemValue}
                    </a>
                    <UpdateDueDateModal
                        applicationProperty={applicationProperty}
                        isOpen={isUpdateDueDateModalOpen}
                        onCancel={onClickUpdateDueDateCancel}
                    />
                </Descriptions.Item>
                <Descriptions.Item label='Inspection Date'>
                    <a onClick={onClickUpdateInspectionDate}>
                        {inspectionDateItemValue}
                    </a>
                    <ValuationInspectionDateModal
                        application={application}
                        applicationProperty={applicationProperty}
                        isOpen={isUpdateInspectionDateModalOpen}
                        onCancel={onClickUpdateInspectionDateCancel}
                    />
                </Descriptions.Item>
                <Descriptions.Item label='Received Date'>
                    <a onClick={onClickUpdateReceivedDate}>
                        {valuationReceivedDateItemValue}
                    </a>
                    <ValuationReceivedDateModal
                        applicationProperty={applicationProperty}
                        isOpen={isUpdateReceivedDateModalOpen}
                        onCancel={onClickUpdateReceivedDateCancel}
                    />
                </Descriptions.Item>
            </Descriptions>
            <Typography.Title className='valuers-title' level={3}>Valuers</Typography.Title>
            <Table
                columns={columns}
                dataSource={propertyValuerValuerPairs}
                pagination={false}
                rowKey='uid'
                size='small'
            />
        </div>
    );
}
